import { Fragment, useMemo } from 'react';
import { Info } from 'lucide-react';
import type { MaturityChartEntry, PickWindowSource } from '@/types/cultivar';
import {
  axisPercent, buildAxis, buildGridlines, colourKeyFor, daysPercent, hasRecordedEnd,
  rootCropType, shortDate, shortDateWithYear, UNCLASSIFIED_CROP_TYPE_ID, windowEnd,
  type CropTypeColourKey, type MaturityGroupBy,
} from '@/utils/maturityChart';

/**
 * The maturity timeline: every cultivar's pick window laid along a calendar
 * axis, coloured by crop type.
 *
 * Plain CSS-positioned bars rather than a charting library — the project has no
 * chart dependency, and a single-axis timeline does not justify adding one.
 *
 * Presentation only: it takes entries and a colour map and renders them. All
 * fetching and filtering sits in the page above, which is what lets the public
 * website mount the same component over `/api/public/maturity-chart`.
 */

interface Props {
  entries: MaturityChartEntry[];
  colours: Map<number, CropTypeColourKey>;
  groupBy: MaturityGroupBy;
  /** Drawn with a tinted row and a heavier label — used to pick one cultivar out of the crowd. */
  highlightCultivarId?: number;
  /** Shown on each row when more than one region is in view. */
  showRegion: boolean;
  /** Shown on each row when more than one season is in view. */
  showSeason: boolean;
  onSelectCultivar?: (cultivarId: number) => void;
}

interface Group {
  key: string;
  label: string;
  colour: string | null;
  entries: MaturityChartEntry[];
}

export function MaturityTimeline({
  entries, colours, groupBy, highlightCultivarId, showRegion, showSeason, onSelectCultivar,
}: Props) {
  const axis = useMemo(() => buildAxis(entries), [entries]);
  const gridlines = useMemo(() => buildGridlines(axis), [axis]);

  const groups = useMemo<Group[]>(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: '', colour: null, entries }];
    }
    const map = new Map<string, Group>();
    for (const e of entries) {
      let key: string;
      let label: string;
      let colour: string | null;
      if (groupBy === 'crop_type') {
        const ck = colourKeyFor(e, colours);
        key = `c${ck.id}`;
        label = ck.name;
        colour = ck.colour;
      } else {
        key = `r${e.growing_region_id}`;
        label = e.region_name ?? `Region ${e.growing_region_id}`;
        colour = null;
      }
      if (!map.has(key)) map.set(key, { key, label, colour, entries: [] });
      map.get(key)!.entries.push(e);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [entries, groupBy, colours]);

  if (!axis) return null;

  const renderBar = (e: MaturityChartEntry) => {
    const isCalculated = e.source === 'calculated';
    const key = colourKeyFor(e, colours);
    const end = windowEnd(e);
    const measuredEnd = hasRecordedEnd(e);
    const left = axisPercent(axis, e.first_pick_date, e.season_year);
    const right = axisPercent(axis, end, e.season_year);
    // A same-day window still needs a visible mark.
    const width = Math.max(right - left, 1.5);
    const highlight = e.cultivar_id === highlightCultivarId;
    const root = rootCropType(e);

    const title = [
      e.cultivar_name ?? `Cultivar ${e.cultivar_id}`,
      root ? `· ${root.name}` : null,
      `· ${e.region_name ?? `Region ${e.growing_region_id}`}`,
      `· season ${e.season_year}`,
      isCalculated
        ? `\nEstimated ${shortDateWithYear(e.first_pick_date)} ±${e.uncertainty_days}d, ${Math.round((e.confidence ?? 0) * 100)}% confidence`
        : `\n${shortDateWithYear(e.first_pick_date)} – ${shortDateWithYear(end)}`,
      measuredEnd
        ? null
        : `\n${WINDOW_SOURCE_NOTE[e.window_source ?? 'default']}${e.window_days != null ? ` (${e.window_days} days)` : ''}`,
      e.source === 'observed' ? `\nFrom ${e.observation_count ?? 0} observation(s)` : null,
    ].filter(Boolean).join(' ');

    const Row = onSelectCultivar ? 'button' : 'div';

    return (
      <Row
        key={`${e.cultivar_id}-${e.growing_region_id}-${e.season_year}-${e.source}`}
        {...(onSelectCultivar
          ? { type: 'button' as const, onClick: () => onSelectCultivar(e.cultivar_id) }
          : {})}
        className={`flex w-full items-center gap-2 py-1.5 text-left ${
          highlight ? 'bg-primary-50 -mx-2 px-2 rounded' : ''
        } ${onSelectCultivar ? 'hover:bg-neutral-50 rounded' : ''}`}
        title={title}
      >
        <div className="w-28 sm:w-52 shrink-0 min-w-0">
          <span
            className={`text-xs truncate block ${
              highlight ? 'font-semibold text-primary-800' : 'text-neutral-800'
            }`}
          >
            {e.cultivar_name || `Cultivar ${e.cultivar_id}`}
          </span>
          {(showRegion || showSeason) && (
            <span className="text-[10px] text-neutral-500 truncate block">
              {showRegion && (e.region_name ?? `Region ${e.growing_region_id}`)}
              {showRegion && showSeason && ' · '}
              {showSeason && e.season_year}
            </span>
          )}
        </div>

        <div className="relative flex-1 h-6 min-w-0">
          {/* Uncertainty band behind the bar, for estimates only. */}
          {isCalculated && e.uncertainty_days > 0 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 h-4 rounded bg-neutral-200/70"
              style={{
                left: `${Math.max(0, left - daysPercent(axis, e.uncertainty_days))}%`,
                width: `${width + 2 * daysPercent(axis, e.uncertainty_days)}%`,
              }}
            />
          )}
          <div
            className="absolute top-1/2 -translate-y-1/2 h-3 rounded"
            style={{
              left: `${left}%`,
              width: `${width}%`,
              // A 2px surface ring keeps two bars that touch from reading as one.
              boxShadow: '0 0 0 2px #ffffff',
              ...(isCalculated
                ? {
                  // Estimates are hollow: the crop-type hue still identifies
                  // them, but they cannot be mistaken for a measurement.
                  backgroundColor: 'transparent',
                  border: `1.5px dashed ${key.colour}`,
                }
                : measuredEnd
                  ? { backgroundColor: key.colour }
                  : {
                    // A recorded start with an assumed length: a solid cap on
                    // the maturity date, then a tint for the window after it.
                    // The start is a measurement; how long it ran is not.
                    backgroundColor: `${key.colour}55`,
                    borderLeft: `4px solid ${key.colour}`,
                  }),
            }}
          />
        </div>

        <div className="w-16 sm:w-24 shrink-0 text-right">
          <span className={`text-xs tabular-nums ${isCalculated ? 'text-neutral-400 italic' : 'text-neutral-600'}`}>
            {shortDate(e.first_pick_date)}
            {isCalculated && '*'}
          </span>
        </div>
      </Row>
    );
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-3 overflow-x-auto">
      <div className="min-w-[480px]">
        {/* Gridlines inset to line up with the bar track between the two labels. */}
        <div className="relative h-4 mb-1" style={{ marginLeft: '7rem', marginRight: '4rem' }}>
          {gridlines.map((g) => (
            <div key={g.key} className="absolute top-0" style={{ left: `${g.left}%` }}>
              <span className="text-[10px] text-neutral-400 -translate-x-1/2 block whitespace-nowrap">
                {g.label}
              </span>
            </div>
          ))}
        </div>

        {groups.map((g) => (
          <Fragment key={g.key}>
            {groupBy !== 'none' && (
              <div className="flex items-center gap-2 pt-3 pb-1 first:pt-0">
                {g.colour && (
                  <span
                    className="inline-block w-3 h-3 rounded-sm shrink-0"
                    style={{ backgroundColor: g.colour }}
                  />
                )}
                <span className="text-xs font-semibold text-neutral-700 truncate">{g.label}</span>
                <span className="text-[10px] text-neutral-400 shrink-0">{g.entries.length}</span>
              </div>
            )}
            {g.entries.map(renderBar)}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * The legend, doubling as a crop-type filter.
 *
 * Present whenever there are two or more crop types — identity on this chart is
 * never carried by colour alone, and the bars' own labels are the other half of
 * that.
 */
export function MaturityLegend({
  colours, counts, selected, onToggle, onShowAll,
}: {
  colours: Map<number, CropTypeColourKey>;
  counts: Map<number, number>;
  selected: Set<number>;
  onToggle: (cropTypeId: number) => void;
  /** Clears the crop-type filter. Offered only while one is applied. */
  onShowAll?: () => void;
}) {
  // Every crop type the dataset has, not just the ones currently drawn — these
  // chips are the filter, so a deselected one has to stay on screen to be
  // switched back on. The "no crop type" bucket is the exception: it appears
  // only when something actually lands in it.
  const shown = [...colours.values()]
    .filter((k) => k.id !== UNCLASSIFIED_CROP_TYPE_ID || (counts.get(k.id) ?? 0) > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (shown.length === 0) return null;

  const anySelected = selected.size > 0;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Without this, getting back to everything after picking two crop types
          means finding and un-clicking both. */}
      {onShowAll && (
        <button
          type="button"
          onClick={onShowAll}
          aria-pressed={!anySelected}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
            !anySelected
              ? 'border-primary-300 bg-primary-50 text-primary-800 font-medium'
              : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
          }`}
        >
          All crop types
        </button>
      )}
      {shown.map((k) => {
        const on = !anySelected || selected.has(k.id);
        return (
          <button
            key={k.id}
            type="button"
            onClick={() => onToggle(k.id)}
            aria-pressed={on}
            className={`flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition-colors ${
              on
                ? 'border-neutral-300 bg-white text-neutral-800'
                : 'border-neutral-200 bg-neutral-50 text-neutral-400'
            }`}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: k.colour, opacity: on ? 1 : 0.35 }}
            />
            <span className="truncate max-w-[9rem]">{k.name}</span>
            {/* A crop type filtered out server-side has no entries to count;
                it reads 0 rather than blank, so the chip still says what
                switching it back on would bring. */}
            <span className="tabular-nums text-neutral-400">{counts.get(k.id) ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Tooltip wording for how a bar's end was arrived at. */
const WINDOW_SOURCE_NOTE: Record<PickWindowSource, string> = {
  recorded: 'Last pick recorded',
  record: 'End assumed from the harvest window on this record',
  cultivar: 'End assumed from the cultivar’s harvest window readings',
  default: 'End assumed from the default harvest window — none recorded for this cultivar',
};

/** Explains the bar styles, and what an estimate is. Sits under the chart. */
export function MaturityChartKey({
  recordedCount, calculatedCount, assumedWindowCount = 0, foldedSeasons = 0,
}: {
  recordedCount: number;
  calculatedCount: number;
  /** Recorded entries whose end comes from a window length, not a last pick. */
  assumedWindowCount?: number;
  /** How many seasons are laid over one calendar; 0 or 1 means none. */
  foldedSeasons?: number;
}) {
  return (
    <div className="text-xs text-neutral-500 space-y-1.5">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 h-2.5 rounded bg-neutral-500" />
          Recorded pick
        </span>
        {assumedWindowCount > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-2.5 rounded bg-neutral-500/35 border-l-4 border-neutral-500" />
            Maturity date + harvest window
          </span>
        )}
        {calculatedCount > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-2.5 rounded border-[1.5px] border-dashed border-neutral-500" />
            Calculated estimate
          </span>
        )}
        <span>
          {recordedCount} recorded
          {calculatedCount > 0 && `, ${calculatedCount} calculated`}
        </span>
      </div>
      {assumedWindowCount > 0 && (
        <p className="flex items-start gap-1">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>
            A tinted bar has a recorded maturity date but no recorded last pick. It starts on the
            maturity date and runs for the cultivar’s harvest window — its Harvest window
            readings, or a default length when none are recorded. Hover a bar to see which.
          </span>
        </p>
      )}
      {foldedSeasons > 1 && (
        <p className="flex items-start gap-1">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>
            {foldedSeasons} seasons are laid over one season’s calendar so they can be compared
            directly; each row names its season.
          </span>
        </p>
      )}
      {calculatedCount > 0 && (
        <p className="flex items-start gap-1">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>
            * A calculated entry has no recorded pick in that region. It is estimated from the
            same cultivar's pick elsewhere this season plus the measured timing difference
            between the two regions, and the grey band shows the spread in that difference.
            Treat them as indicative, not as measurements.
          </span>
        </p>
      )}
    </div>
  );
}

export default MaturityTimeline;
